import { type FormEvent, type ReactElement, useEffect, useState } from "react";

import {
  APIError,
  api,
  type CheckoutLease,
  type Document,
  type ManagedPublication,
  type Organization,
  type Project,
  type RecoveryCopy,
  type Version,
} from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import { formatBytes } from "./Documents";

interface DetailData {
  document: Document;
  organization: Organization;
  project: Project;
  versions: Version[];
  recoveryCopies: RecoveryCopy[];
}

export function DocumentDetail({
  documentId,
}: {
  documentId: string;
}): ReactElement {
  const { actor } = useAuth();
  const [data, setData] = useState<DetailData | null>(null);
  const [error, setError] = useState<APIError | Error | null>(null);
  const [breaking, setBreaking] = useState(false);
  const [breakError, setBreakError] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [publication, setPublication] = useState<ManagedPublication | null>(
    null,
  );
  const [publicationChecked, setPublicationChecked] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [publishBusy, setPublishBusy] = useState(false);
  const [publishError, setPublishError] = useState<string | null>(null);
  const [publishNotice, setPublishNotice] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let active = true;
    setData(null);
    setError(null);
    Promise.all([
      api.document(documentId),
      api.versions(documentId),
      api.recoveryCopies(documentId),
    ])
      .then(async ([document, versions, recoveryCopies]) => {
        const project = await api.project(document.project_id);
        const organization = await api.organization(project.organization_id);
        return {
          document,
          organization,
          project,
          versions: versions.items,
          recoveryCopies: recoveryCopies.items,
        };
      })
      .then((result) => {
        if (active) setData(result);
      })
      .catch((cause: APIError | Error) => {
        if (active) setError(cause);
      });
    return () => {
      active = false;
    };
  }, [documentId]);

  useEffect(() => {
    let active = true;
    setPublication(null);
    setPublicationChecked(false);
    api
      .publication(documentId)
      .then((result) => {
        if (!active) return;
        setPublication(result);
        setPublicationChecked(true);
      })
      .catch((cause: unknown) => {
        if (!active) return;
        if (cause instanceof APIError && cause.code === "not_found") {
          setPublication(null);
          setPublicationChecked(true);
          return;
        }
        setPublicationChecked(true);
      });
    return () => {
      active = false;
    };
  }, [documentId]);

  const role = data?.organization.actor_role ?? null;
  const canBreak = role === "owner" || role === "admin";
  const canPublish = role === "owner" || role === "admin";
  const lease = data?.document.checkout ?? null;
  const leaseState = effectiveLeaseState(lease);
  const isOwnLease = Boolean(lease && actor && lease.owner.id === actor.id);

  async function breakCheckout(reason: string): Promise<void> {
    if (!data || !lease) return;
    setBreaking(true);
    setBreakError(null);
    try {
      const updated = await api.breakCheckout(
        data.document.id,
        lease.id,
        reason,
      );
      setData({ ...data, document: { ...data.document, checkout: updated } });
      setConfirmOpen(false);
    } catch (cause) {
      if (cause instanceof APIError) {
        setBreakError(`${cause.message} · Request ${cause.requestId}`);
      } else {
        setBreakError("The checkout could not be broken. Try again.");
      }
    } finally {
      setBreaking(false);
    }
  }

  async function downloadVersion(version: Version): Promise<void> {
    if (!data) return;
    setDownloadingId(version.id);
    setDownloadError(null);
    try {
      await api.downloadVersion(
        data.document.id,
        version.id,
        `${data.document.name}-v${version.sequence}.FCStd`,
      );
    } catch (cause) {
      setDownloadError(downloadMessage(cause));
    } finally {
      setDownloadingId(null);
    }
  }

  async function downloadRecovery(copy: RecoveryCopy): Promise<void> {
    if (!data) return;
    setDownloadingId(copy.id);
    setDownloadError(null);
    try {
      await api.downloadRecoveryCopy(
        data.document.id,
        copy.id,
        `${data.document.name}-recovery-${copy.id.slice(0, 8)}.FCStd`,
      );
    } catch (cause) {
      setDownloadError(downloadMessage(cause));
    } finally {
      setDownloadingId(null);
    }
  }

  if (error) {
    const notFound = error instanceof APIError && error.code === "not_found";
    return (
      <main className="page">
        <section className="empty-state error-state" role="alert">
          <p className="eyebrow">{notFound ? "Not found" : "Unable to load"}</p>
          <h1>
            {notFound
              ? "This document is unavailable."
              : "We couldn’t open this document."}
          </h1>
          <p>
            {notFound
              ? "It may have moved, or your organization access may have changed."
              : error.message}
          </p>
          <a className="button button-secondary" href="/workspace">
            Return to documents
          </a>
        </section>
      </main>
    );
  }

  if (!data) {
    return (
      <main className="page">
        <section
          className="detail-skeleton"
          aria-label="Loading document"
          aria-busy="true"
        />
      </main>
    );
  }

  const { document, versions, recoveryCopies } = data;

  return (
    <main className="page detail-page">
      <div className="breadcrumbs">
        <a
          href={`/workspace/organizations/${encodeURIComponent(data.project.organization_id)}/projects/${encodeURIComponent(data.project.id)}`}
        >
          Documents
        </a>
        <span aria-hidden="true">/</span>
        <span>{document.name}</span>
      </div>

      <header className="detail-heading">
        <div className="detail-title">
          <span className="detail-glyph" aria-hidden="true">
            ◇
          </span>
          <div>
            <p className="eyebrow">{data.project.name}</p>
            <h1>{document.name}</h1>
            <p className="document-path">{document.relative_path}</p>
          </div>
        </div>
        {document.current_version && (
          <button
            className="button button-primary"
            disabled={downloadingId !== null}
            onClick={() => void downloadVersion(document.current_version!)}
            type="button"
          >
            {downloadingId === document.current_version.id
              ? "Downloading…"
              : "Download current"}
          </button>
        )}
      </header>

      {downloadError && (
        <section className="alert alert-danger" role="alert">
          {downloadError}
        </section>
      )}

      <section
        className={`lease-panel lease-panel-${leaseState}`}
        aria-labelledby="checkout-heading"
      >
        <div>
          <p className="eyebrow">Edit coordination</p>
          <h2 id="checkout-heading">{leaseHeading(leaseState, isOwnLease)}</h2>
          <p>{leaseDescription(lease, leaseState, isOwnLease)}</p>
        </div>
        <div className="lease-actions">
          {lease && (
            <span className={`state-pill state-${leaseState}`}>
              {leaseState.replace("_", " ")}
            </span>
          )}
          {lease && canBreak && leaseState === "active" && (
            <button
              className="button button-danger"
              onClick={() => setConfirmOpen(true)}
              type="button"
            >
              Break checkout
            </button>
          )}
        </div>
      </section>

      {leaseState === "expired" && isOwnLease && (
        <section className="alert alert-warning" role="status">
          <strong>Your edit lease expired.</strong> Save the work from VibeCAD
          as a recovery copy. It cannot become the current version without a new
          checkout.
        </section>
      )}
      {lease && leaseState === "broken" && isOwnLease && (
        <section className="alert alert-warning" role="status">
          <strong>Your checkout was broken.</strong> Preserve any local edits as
          a recovery copy; publishing them as current is blocked.
        </section>
      )}

      {publicationChecked && (
        <PublicationPanel
          canPublish={canPublish}
          copied={copied}
          error={publishError}
          notice={publishNotice}
          publication={publication}
          versions={versions}
          onCopy={async () => {
            if (!publication) return;
            try {
              await navigator.clipboard.writeText(
                `${window.location.origin}/m/${publication.slug}`,
              );
              setCopied(true);
            } catch {
              setCopied(false);
              setPublishError("The public link could not be copied.");
            }
          }}
          onOpen={() => {
            setPublishError(null);
            setPublishOpen(true);
          }}
          onUnpublish={async () => {
            setPublishBusy(true);
            setPublishError(null);
            try {
              await api.unpublishDocument(document.id);
              setPublication(null);
              setPublishNotice("The public listing was unpublished.");
              setCopied(false);
            } catch (cause) {
              setPublishError(publicationMessage(cause));
            } finally {
              setPublishBusy(false);
            }
          }}
          busy={publishBusy}
        />
      )}

      <section className="metric-strip" aria-label="Document summary">
        <div>
          <span>Current version</span>
          <strong>
            {document.current_version
              ? `v${document.current_version.sequence}`
              : "—"}
          </strong>
        </div>
        <div>
          <span>Total versions</span>
          <strong>{versions.length}</strong>
        </div>
        <div>
          <span>Recovery copies</span>
          <strong>{recoveryCopies.length}</strong>
        </div>
        <div>
          <span>Your role</span>
          <strong className="capitalize">{role ?? "viewer"}</strong>
        </div>
      </section>

      <section className="content-section" aria-labelledby="versions-heading">
        <div className="section-heading">
          <div>
            <p className="eyebrow">Immutable history</p>
            <h2 id="versions-heading">Versions</h2>
          </div>
          <span>{versions.length} retained</span>
        </div>
        {versions.length === 0 ? (
          <div className="inline-empty">No versions have been published.</div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Version</th>
                  <th>Change</th>
                  <th>Author</th>
                  <th>Created</th>
                  <th>Integrity</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {versions.map((version) => (
                  <tr key={version.id}>
                    <td>
                      <span className="version-number">
                        v{version.sequence}
                      </span>
                    </td>
                    <td>
                      <strong>
                        {version.change_message || "No change note"}
                      </strong>
                      <small>{formatBytes(version.size_bytes)}</small>
                    </td>
                    <td>{version.created_by.display_name}</td>
                    <td>
                      <time dateTime={version.created_at}>
                        {formatDateTime(version.created_at)}
                      </time>
                    </td>
                    <td>
                      <code title={version.sha256}>
                        {version.sha256.slice(0, 10)}…
                      </code>
                    </td>
                    <td>
                      <button
                        className="table-action"
                        disabled={downloadingId !== null}
                        onClick={() => void downloadVersion(version)}
                        type="button"
                      >
                        {downloadingId === version.id
                          ? "Downloading…"
                          : "Download"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="content-section" aria-labelledby="recovery-heading">
        <div className="section-heading">
          <div>
            <p className="eyebrow">Preserved work</p>
            <h2 id="recovery-heading">Recovery copies</h2>
          </div>
          <span>Never promoted automatically</span>
        </div>
        {recoveryCopies.length === 0 ? (
          <div className="inline-empty">
            No lost-lock work has needed recovery.
          </div>
        ) : (
          <div className="recovery-list">
            {recoveryCopies.map((copy) => (
              <article className="recovery-card" key={copy.id}>
                <span className="recovery-icon" aria-hidden="true">
                  ↺
                </span>
                <div>
                  <strong>{humanize(copy.reason)}</strong>
                  <p>
                    {copy.note || "Preserved from a non-current edit session."}
                  </p>
                </div>
                <div className="recovery-meta">
                  <span>{copy.created_by.display_name}</span>
                  <time dateTime={copy.created_at}>
                    {formatDateTime(copy.created_at)}
                  </time>
                </div>
                <button
                  className="button button-quiet"
                  disabled={downloadingId !== null}
                  onClick={() => void downloadRecovery(copy)}
                  type="button"
                >
                  {downloadingId === copy.id ? "Downloading…" : "Download"}
                </button>
              </article>
            ))}
          </div>
        )}
      </section>

      {confirmOpen && lease && (
        <BreakCheckoutDialog
          busy={breaking}
          error={breakError}
          owner={lease.owner.display_name}
          onCancel={() => {
            if (!breaking) setConfirmOpen(false);
          }}
          onConfirm={breakCheckout}
        />
      )}
      {publishOpen && (
        <PublishDialog
          busy={publishBusy}
          documentName={document.name}
          error={publishError}
          existing={publication}
          versions={versions}
          onCancel={() => {
            if (!publishBusy) setPublishOpen(false);
          }}
          onSubmit={async (input) => {
            setPublishBusy(true);
            setPublishError(null);
            try {
              const next = publication
                ? await api.updatePublication(document.id, input)
                : await api.publishDocument(document.id, {
                    title: input.title ?? document.name,
                    version_id: input.version_id,
                    summary: input.summary,
                    license: input.license,
                  });
              setPublication(next);
              setPublishOpen(false);
              setPublishNotice(
                publication
                  ? "Public listing updated."
                  : "Document published to Explore.",
              );
            } catch (cause) {
              setPublishError(publicationMessage(cause));
            } finally {
              setPublishBusy(false);
            }
          }}
        />
      )}
    </main>
  );
}

function BreakCheckoutDialog({
  busy,
  error,
  owner,
  onCancel,
  onConfirm,
}: {
  busy: boolean;
  error: string | null;
  owner: string;
  onCancel: () => void;
  onConfirm: (reason: string) => Promise<void>;
}): ReactElement {
  const [reason, setReason] = useState("");
  const valid = reason.trim().length >= 10;

  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (valid) void onConfirm(reason.trim());
  }

  return (
    <div
      className="dialog-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onCancel();
      }}
    >
      <section
        aria-labelledby="break-title"
        aria-modal="true"
        className="dialog"
        role="dialog"
      >
        <span className="dialog-warning" aria-hidden="true">
          !
        </span>
        <p className="eyebrow">High-impact action</p>
        <h2 id="break-title">Break {owner}’s checkout?</h2>
        <p>
          Their local work will lose publication rights immediately. VibeCAD can
          preserve it only as a non-current recovery copy. This action is
          written to the audit trail.
        </p>
        <form onSubmit={submit}>
          <label htmlFor="break-reason">Reason for breaking the checkout</label>
          <textarea
            id="break-reason"
            autoFocus
            maxLength={1000}
            minLength={10}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Explain why this edit lease must be interrupted…"
            required
            rows={4}
            value={reason}
          />
          <small>{reason.trim().length}/10 minimum characters</small>
          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}
          <div className="dialog-actions">
            <button
              className="button button-quiet"
              disabled={busy}
              onClick={onCancel}
              type="button"
            >
              Keep checkout
            </button>
            <button
              className="button button-danger"
              disabled={!valid || busy}
              type="submit"
            >
              {busy ? "Breaking…" : "Confirm break"}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}

function effectiveLeaseState(
  lease: CheckoutLease | null,
): CheckoutLease["state"] | "available" {
  if (!lease) return "available";
  if (
    lease.state === "active" &&
    new Date(lease.expires_at).getTime() <= Date.now()
  )
    return "expired";
  return lease.state;
}

function leaseHeading(
  state: ReturnType<typeof effectiveLeaseState>,
  own: boolean,
): string {
  if (state === "available" || state === "released" || state === "checked_in")
    return "Available for checkout";
  if (state === "active")
    return own ? "You have this document checked out" : "An editor is active";
  if (state === "expired")
    return own ? "Your checkout has expired" : "The last checkout expired";
  return own ? "Your checkout was broken" : "The last checkout was broken";
}

function leaseDescription(
  lease: CheckoutLease | null,
  state: ReturnType<typeof effectiveLeaseState>,
  own: boolean,
): string {
  if (!lease || state === "available")
    return "No active writer lease. Open the document in VibeCAD to begin editing.";
  if (state === "active") {
    return `${own ? "Your" : `${lease.owner.display_name}’s`} exclusive lease expires ${formatDateTime(lease.expires_at)}.`;
  }
  if (state === "expired")
    return "This lease cannot be renewed. Local changes must be preserved as a recovery copy.";
  if (state === "broken")
    return lease.terminal_reason || "An administrator ended this checkout.";
  return `The previous checkout is ${state.replace("_", " ")}.`;
}

function downloadMessage(cause: unknown): string {
  if (cause instanceof APIError) {
    return `${cause.message} · Request ${cause.requestId}`;
  }
  return "The file could not be downloaded. Check your connection and try again.";
}

function publicationMessage(cause: unknown): string {
  if (cause instanceof APIError) {
    if (cause.code === "validation_failed") {
      return "Publish a versioned document with a title and plaintext summary.";
    }
    if (cause.code === "already_published") {
      return "This document already has an active public listing.";
    }
    if (cause.code === "forbidden") {
      return "Only owners and admins can change the public listing.";
    }
    return `${cause.message} · Request ${cause.requestId}`;
  }
  return "The public listing could not be updated.";
}

function PublicationPanel({
  busy,
  canPublish,
  copied,
  error,
  notice,
  onCopy,
  onOpen,
  onUnpublish,
  publication,
  versions,
}: {
  busy: boolean;
  canPublish: boolean;
  copied: boolean;
  error: string | null;
  notice: string | null;
  onCopy: () => Promise<void>;
  onOpen: () => void;
  onUnpublish: () => Promise<void>;
  publication: ManagedPublication | null;
  versions: Version[];
}): ReactElement {
  return (
    <section className="publication-panel" aria-labelledby="publish-heading">
      <div>
        <p className="eyebrow">Public sharing</p>
        <h2 id="publish-heading">
          {publication ? "Published on Explore" : "Not published"}
        </h2>
        <p>
          {publication
            ? `Listed as ${publication.title} · v${publication.version_sequence}.`
            : canPublish
              ? "Publish one immutable version for anyone to download from Explore."
              : "This document is private. Only owners and admins can publish it."}
        </p>
        {publication && <p className="document-path">/m/{publication.slug}</p>}
      </div>
      <div className="lease-actions">
        {publication && (
          <>
            <a
              className="button button-secondary"
              href={`/m/${publication.slug}`}
            >
              View public page
            </a>
            <button
              className="button button-quiet"
              onClick={() => void onCopy()}
              type="button"
            >
              {copied ? "Copied public link" : "Copy public link"}
            </button>
          </>
        )}
        {canPublish && (
          <>
            <button
              className="button button-primary"
              disabled={busy || (!publication && versions.length === 0)}
              onClick={onOpen}
              type="button"
            >
              {publication ? "Update listing" : "Publish"}
            </button>
            {publication && (
              <button
                className="button button-danger"
                disabled={busy}
                onClick={() => void onUnpublish()}
                type="button"
              >
                Unpublish
              </button>
            )}
          </>
        )}
      </div>
      {notice && (
        <div className="alert alert-success" role="status">
          {notice}
        </div>
      )}
      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}
    </section>
  );
}

function PublishDialog({
  busy,
  documentName,
  error,
  existing,
  onCancel,
  onSubmit,
  versions,
}: {
  busy: boolean;
  documentName: string;
  error: string | null;
  existing: ManagedPublication | null;
  onCancel: () => void;
  onSubmit: (input: {
    title?: string;
    version_id?: string;
    summary?: string;
    license?: string;
  }) => Promise<void>;
  versions: Version[];
}): ReactElement {
  const [title, setTitle] = useState(existing?.title || documentName);
  const [summary, setSummary] = useState(existing?.summary ?? "");
  const [license, setLicense] = useState(existing?.license ?? "");
  const [versionId, setVersionId] = useState(
    existing?.version_id || versions[0]?.id || "",
  );

  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (!title.trim() || !versionId) return;
    void onSubmit({
      title: title.trim(),
      version_id: versionId,
      summary: summary.trim(),
      license: license.trim(),
    });
  }

  return (
    <div
      className="dialog-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onCancel();
      }}
    >
      <section
        aria-labelledby="publish-title"
        aria-modal="true"
        className="dialog"
        role="dialog"
      >
        <p className="eyebrow">Public listing</p>
        <h2 id="publish-title">
          {existing ? "Update public listing" : "Publish this document"}
        </h2>
        <p>
          Only the selected immutable version is shared. Private checkout,
          recovery, and team data stay hidden.
        </p>
        <form onSubmit={submit}>
          <label htmlFor="publish-title-field">Title</label>
          <input
            id="publish-title-field"
            maxLength={120}
            onChange={(event) => setTitle(event.target.value)}
            required
            value={title}
          />
          <label htmlFor="publish-version">Listed version</label>
          <select
            id="publish-version"
            onChange={(event) => setVersionId(event.target.value)}
            required
            value={versionId}
          >
            {versions.map((version) => (
              <option key={version.id} value={version.id}>
                v{version.sequence} ·{" "}
                {version.change_message || "No change note"}
              </option>
            ))}
          </select>
          <label htmlFor="publish-summary">Summary</label>
          <textarea
            id="publish-summary"
            maxLength={500}
            onChange={(event) => setSummary(event.target.value)}
            rows={3}
            value={summary}
          />
          <label htmlFor="publish-license">License</label>
          <input
            id="publish-license"
            maxLength={80}
            onChange={(event) => setLicense(event.target.value)}
            placeholder="Optional, e.g. CC-BY-4.0"
            value={license}
          />
          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}
          <div className="dialog-actions">
            <button
              className="button button-quiet"
              disabled={busy}
              onClick={onCancel}
              type="button"
            >
              Cancel
            </button>
            <button
              className="button button-primary"
              disabled={busy || !title.trim() || !versionId}
              type="submit"
            >
              {busy
                ? existing
                  ? "Updating…"
                  : "Publishing…"
                : existing
                  ? "Save listing"
                  : "Publish to Explore"}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}

function formatDateTime(value: string): string {
  return new Date(value).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

function humanize(value: string): string {
  return value
    .replaceAll("_", " ")
    .replace(/^./, (first) => first.toUpperCase());
}
